import React, { useState, useRef } from "react";
import { useDispatch, useSelector, useStore } from "react-redux";
import {
  type GridColumns,
  GridToolbarContainer,
  GridToolbarExport,
  GridToolbarColumnsButton,
  GridToolbarDensitySelector,
} from "@material-ui/x-grid";
import XGrid from "src/components/CSVFormattedXGrid";
import Box from "@material-ui/core/Box";

import { formatAbsCurrency, formatProfitCurrency } from "src/utils/formatter";
import {
  makeSalesStyle,
  makeTransactionsStyle,
  makeProfitStyle,
} from "src/utils/gridCellStyles";
import makeRenderCell from "src/utils/makeRenderCell";
import { renderHeaderLabel } from "src/pages/TransactionDetails/Sales/columns";
import ConfirmReturnDetailsDialog from "src/pages/TransactionDetails/Sales/ConfirmReturnDetailsDialog";
import { valueGetterWithDefault } from "src/utils/valueGetter";
import { userGetInventoryTypeSelector } from "src/store/system/selector";
import { EditDialog as SaleEditDialog } from "src/pages/SalesPage/EditDialog";
import ConfirmDialog from "src/components/ConfirmDialog";
import { salesSelector, getSaleById } from "src/store/sale/selector";
import {
  updateItem as updateSale,
  updateBundledItems as updateBundleSales,
  deleteBundledItems as deleteBundledSales,
  deleteSalesOptimistic,
  deleteReturnOptimistic,
  moveBundledItems as moveBundledSales,
  moveItem as moveSale,
  recordReturn,
} from "src/store/sale/actions";
import { parseError } from "src/utils";
import { toast } from "react-toastify";
import type { Sale } from "src/interfaces/sale.interface";
import type { Transaction } from "src/interfaces/transaction.interface";
import SaleEdit, {
  type DialogState as SaleEditDialogState,
} from "src/pages/TransactionDetails/Sales/Edit";
import { setActiveDialog } from "src/store/adminHtml/actions";
import { calculateGrossProfit } from "src/utils/sales";

import { ProfitLossReportData } from "./report";
import { useOpenDialog } from "./useOpenDialog";
import Details from "./Details";

export interface StatementDrillSalesType {
  type: "sales";
  platform: string;
}

export interface StatementDrillExpenseType {
  type: "expense";
  name: string;
}

export interface StatementDrillTransactionType {
  type: "transaction";
  name: string;
}

export type StatementDrillType =
  | StatementDrillSalesType
  | StatementDrillExpenseType
  | StatementDrillTransactionType
  | null;

export interface StatementDrillProps {
  data: ProfitLossReportData;
  type?: StatementDrillType;
  onReload?: () => void;
  onClose?: () => void;
}

const SalesColumns: GridColumns = [
  {
    field: "item_title",
    headerName: "Item Title",
    width: 200,
    renderHeader: renderHeaderLabel,
  },
  {
    field: "department",
    headerName: "Department",
    width: 150,
    renderHeader: renderHeaderLabel,
    valueGetter: valueGetterWithDefault(""),
  },
  {
    field: "category",
    headerName: "Category",
    width: 200,
    renderHeader: renderHeaderLabel,
    valueGetter: valueGetterWithDefault(""),
  },
  {
    field: "sub_category",
    headerName: "Sub Category",
    width: 200,
    renderHeader: renderHeaderLabel,
    valueGetter: valueGetterWithDefault(""),
  },
  {
    field: "brand",
    headerName: "Brand",
    width: 150,
    renderHeader: renderHeaderLabel,
    valueGetter: valueGetterWithDefault(""),
  },
  {
    field: "location",
    headerName: "Location",
    width: 200,
    renderHeader: renderHeaderLabel,
    valueGetter: valueGetterWithDefault(""),
  },
  {
    field: "sku",
    headerName: "SKU",
    width: 150,
    renderHeader: renderHeaderLabel,
    valueGetter: valueGetterWithDefault(""),
  },
  {
    field: "purchase_date",
    headerName: "Purchase Date",
    type: "date",
    width: 200,
    renderHeader: renderHeaderLabel,
  },
  {
    field: "list_date",
    headerName: "List Date",
    type: "date",
    width: 200,
    renderHeader: renderHeaderLabel,
  },
  {
    field: "return_date",
    headerName: "Return Date",
    type: "date",
    width: 200,
    renderHeader: renderHeaderLabel,
  },
  {
    field: "sale_date",
    headerName: "Sale Date",
    type: "date",
    width: 200,
    renderHeader: renderHeaderLabel,
  },
  {
    field: "days_on_platform",
    headerName: "Days On Platform",
    type: "number",
    width: 220,
    renderHeader: renderHeaderLabel,
  },
  {
    field: "sale_price",
    headerName: "Sale Price",
    type: "number",
    width: 200,
    renderHeader: renderHeaderLabel,
    renderCell: makeRenderCell({
      valueFormatter: formatAbsCurrency,
      style: makeSalesStyle,
    }),
  },
  {
    field: "purchase_price",
    headerName: "Purchase Price",
    type: "number",
    width: 220,
    renderHeader: renderHeaderLabel,
    renderCell: makeRenderCell({
      valueFormatter: formatAbsCurrency,
      style: makeTransactionsStyle,
    }),
  },
  {
    field: "shipping_cost",
    headerName: "Shipping Costs",
    type: "number",
    width: 200,
    valueGetter: (params) =>
      (params.value || 0) + (params.row.shipping_cost_analytics || 0),
    renderHeader: renderHeaderLabel,
    renderCell: makeRenderCell({
      valueFormatter: formatAbsCurrency,
      style: makeTransactionsStyle,
    }),
  },
  {
    field: "extra_shipping_cost",
    headerName: "Return Shipping Cost",
    type: "number",
    width: 200,
    renderHeader: renderHeaderLabel,
    renderCell: makeRenderCell({
      valueFormatter: formatAbsCurrency,
      style: makeTransactionsStyle,
    }),
  },
  {
    field: "extra_transaction_fees",
    headerName: "Return Transaction Fees",
    type: "number",
    width: 200,
    renderHeader: renderHeaderLabel,
    renderCell: makeRenderCell({
      valueFormatter: formatAbsCurrency,
      style: makeTransactionsStyle,
    }),
  },
  {
    field: "transaction_fees",
    headerName: "Transaction Fees",
    type: "number",
    width: 220,
    renderHeader: renderHeaderLabel,
    renderCell: makeRenderCell({
      valueFormatter: formatAbsCurrency,
      style: makeTransactionsStyle,
    }),
  },
  {
    field: "other_fees",
    headerName: "Other Costs (From Expense Detail)",
    type: "number",
    width: 285,
    renderHeader: renderHeaderLabel,
    renderCell: makeRenderCell({
      valueFormatter: formatAbsCurrency,
      style: makeTransactionsStyle,
    }),
  },
  {
    field: "gross_profit",
    headerName: "Gross profit",
    type: "number",
    width: 200,
    valueGetter: (params) => calculateGrossProfit(params.row),
    renderHeader: renderHeaderLabel,
    renderCell: makeRenderCell({
      valueFormatter: formatProfitCurrency,
      style: makeProfitStyle,
    }),
  },
  {
    field: "sale_state",
    headerName: "Sale State",
    width: 200,
    renderHeader: renderHeaderLabel,
    valueGetter: valueGetterWithDefault(""),
  },
  {
    field: "sales_tax",
    headerName: "Sales Tax",
    type: "number",
    width: 200,
    renderHeader: renderHeaderLabel,
    renderCell: makeRenderCell({
      valueFormatter: formatAbsCurrency,
      style: makeSalesStyle,
    }),
  },
  {
    field: "liable_to_pay",
    headerName: "Liable To Pay",
    width: 200,
    type: "boolean",
    renderHeader: renderHeaderLabel,
  },
  {
    field: "platforms_listed",
    headerName: "Platforms Listed",
    width: 300,
    renderHeader: renderHeaderLabel,
    valueFormatter: ({ value }) => {
      if (Array.isArray(value)) {
        return `${value.join(", ")}`;
      }
      return "";
    },
  },
  {
    field: "sale_platform",
    headerName: "Sale Platform",
    width: 200,
    renderHeader: renderHeaderLabel,
  },
  {
    field: "notes",
    headerName: "Notes",
    width: 200,
    renderHeader: renderHeaderLabel,
    valueGetter: valueGetterWithDefault(""),
  },
];
const SalesColumnsCashIgnored = [
  "purchase_date",
  "purchase_price",
  "platforms_listed",
];
const SalesColumnsCash = SalesColumns.filter(
  ({ field }) => !SalesColumnsCashIgnored.includes(field)
);

const TransactionsColumns: GridColumns = [
  {
    field: "account",
    headerName: "General Ledger Account",
    width: 250,
    renderHeader: renderHeaderLabel,
  },
  {
    field: "name",
    headerName: "Charge Description",
    width: 200,
    type: "string",
  },
  {
    field: "amount",
    headerName: "Amount",
    width: 200,
    type: "number",
    renderCell: makeRenderCell({
      valueFormatter: formatAbsCurrency,
      style: makeTransactionsStyle,
    }),
  },
  { field: "date", headerName: "Date", width: 200, type: "date" },
  {
    field: "description",
    headerName: "Description",
    width: 250,
    valueGetter: valueGetterWithDefault(""),
  },
  {
    field: "integration_name",
    headerName: "Bank Account",
    width: 200,
    valueGetter: valueGetterWithDefault(""),
  },
];

function StatementGridTableToolbar() {
  return (
    <GridToolbarContainer>
      <GridToolbarColumnsButton />
      <GridToolbarDensitySelector />
      <GridToolbarExport />
    </GridToolbarContainer>
  );
}

function EditableTransactionsXGrid({
  rows,
  onReload,
}: {
  rows: Transaction[];
  onReload: StatementDrillProps["onReload"];
}) {
  const dispatch = useDispatch();
  const openDialog = useOpenDialog();
  const [dialog, setDialog] = useState<SaleEditDialogState | undefined>(
    undefined
  );
  return (
    <>
      <SaleEdit
        value={dialog}
        onChange={(dialog) => {
          setDialog(dialog);
          if (!dialog && onReload) onReload();
        }}
        onChanged={() => onReload?.()}
      />
      <XGrid
        rows={rows}
        columns={TransactionsColumns}
        pagination
        disableSelectionOnClick
        components={{
          Toolbar: StatementGridTableToolbar,
        }}
        onCellDoubleClick={async (params) => {
          if (params.row?.type === "sale") {
            setDialog({ type: "edit", id: params.row.sale_id });
          } else if (params.row?.journal_entry_id) {
            dispatch(
              setActiveDialog("add_journal", {
                id: params.row?.journal_entry_id,
              })
            );
          } else {
            const row = params.row as Transaction;
            // Defer to the shared root edit-transaction dialog, which loads the
            // full split (sibling rows) and owns delete/read-only handling.
            const result = await openDialog("transaction", row.id);
            if (result.status === "updated" && onReload) onReload();
          }
        }}
      />
    </>
  );
}

function ExpenseStatementDrill({
  data,
  name,
  onReload,
}: {
  data: ProfitLossReportData;
  name: string;
  onReload: StatementDrillProps["onReload"];
}) {
  const rowsTransactions = data.transactionsListByExpense?.[name];

  return (
    <Box height="75vh">
      {!!rowsTransactions && (
        <Box height="100%" mt={0}>
          <EditableTransactionsXGrid
            rows={rowsTransactions}
            onReload={onReload}
          />
        </Box>
      )}
    </Box>
  );
}

function StatementDrillSalesTableEditDialog({
  sale,
  onSaleChange,
  onClose,
  onReload,
}: {
  sale?: Sale;
  onSaleChange: (sale: Sale) => void;
  onClose: (reload?: boolean) => void;
  onReload?: () => void;
}) {
  const store = useStore();
  const dispatch = useDispatch();
  const [confirmOpen, setConfirmOpen] = useState<
    | { type: "sale"; id: string }
    | { type: "return"; id: string }
    | { type: "bundle"; bundle: any }
    | { type: "return-details"; id: string }
    | undefined
  >(undefined);
  const { loading } = useSelector(salesSelector);
  const hasChangedRef = useRef(false);
  const pendingMutationRef = useRef<Promise<unknown> | undefined>(undefined);

  const trackMutation = (mutation: unknown) => {
    pendingMutationRef.current = Promise.resolve(mutation);
  };

  return (
    <>
      <ConfirmReturnDetailsDialog
        selectedUnreviewedReturnId={
          confirmOpen?.type === "return-details" ? confirmOpen.id : undefined
        }
        onClose={() => setConfirmOpen(undefined)}
      />
      <ConfirmDialog
        open={
          confirmOpen?.type === "sale" ||
          confirmOpen?.type === "return" ||
          confirmOpen?.type === "bundle"
        }
        title="Confirm"
        text={
          confirmOpen?.type === "sale"
            ? "Are you sure you want to delete the sale?"
            : confirmOpen?.type === "return"
            ? "Are you sure you want to delete the return?"
            : confirmOpen?.type === "bundle"
            ? "Are you sure you want to delete the bundled sales?"
            : undefined
        }
        onCancel={() => setConfirmOpen(undefined)}
        onConfirm={async () => {
          if (!confirmOpen) return;
          try {
            if (confirmOpen.type === "sale") {
              await dispatch(deleteSalesOptimistic([confirmOpen.id]));
            } else if (confirmOpen.type === "return") {
              await dispatch(deleteReturnOptimistic(confirmOpen.id));
            } else if (confirmOpen.type === "bundle") {
              await dispatch(deleteBundledSales(confirmOpen.bundle));
            }
            toast.success("Sale has been deleted successfully");
            setConfirmOpen(undefined);
            onClose(true);
            hasChangedRef.current = false;
          } catch (error) {
            toast.error(parseError(error));
          }
        }}
      />
      <SaleEditDialog
        data={sale}
        open={!!sale}
        loading={loading}
        onClose={() => {
          if (loading) return;
          const shouldReload = hasChangedRef.current;
          const pending = pendingMutationRef.current;
          hasChangedRef.current = false;
          pendingMutationRef.current = undefined;
          // Immediate reload reads optimistic Redux from the thunk's sync apply.
          onClose(shouldReload);
          if (shouldReload && pending) {
            // On write failure the thunk reverts Redux; rebuild P&L only (do not
            // call onClose again — the dialog is already closed).
            void pending.then(
              () => undefined,
              () => onReload?.()
            );
          }
        }}
        onSave={(s) => {
          hasChangedRef.current = true;
          trackMutation(
            Promise.resolve(dispatch(updateSale(s))).then(
              () => {
                toast.success("Sale has been updated successfully");
              },
              (error) => {
                toast.error(parseError(error));
                throw error;
              }
            )
          );
        }}
        onSaveBundle={(sales) => {
          hasChangedRef.current = true;
          trackMutation(
            Promise.resolve(dispatch(updateBundleSales(sales))).then(
              () => {
                toast.success("Sale has been updated successfully");
              },
              (error) => {
                toast.error(parseError(error));
                throw error;
              }
            )
          );
        }}
        onMove={(sale) => {
          hasChangedRef.current = true;
          trackMutation(
            Promise.resolve(dispatch(moveSale(sale))).then(
              () => {
                toast.success("Sale has been updated successfully");
              },
              (error) => {
                toast.error(parseError(error));
                throw error;
              }
            )
          );
        }}
        onMoveBundle={(sales) => {
          hasChangedRef.current = true;
          trackMutation(
            Promise.resolve(dispatch(moveBundledSales(sales))).then(
              () => {
                toast.success("Sale has been updated successfully");
              },
              (error) => {
                toast.error(parseError(error));
                throw error;
              }
            )
          );
        }}
        onReturn={(returnRecord, sale, inventory) => {
          hasChangedRef.current = true;
          trackMutation(
            Promise.resolve(
              dispatch(recordReturn({ returnRecord, sale, inventory }))
            ).then(
              () => {
                toast.success("Sale has been updated successfully");
              },
              (error) => {
                toast.error(parseError(error));
                throw error;
              }
            )
          );
        }}
        onDelete={() => {
          if (!sale) return;
          setConfirmOpen({ type: "sale", id: sale.id });
        }}
        onDeleteBundle={(bundle) => {
          setConfirmOpen({ type: "bundle", bundle });
        }}
        onDeleteReturn={(payload) => {
          if (!sale) return;
          setConfirmOpen({ type: "return", id: sale.id });
        }}
        editReturn={(sale, unreviewed) => {
          if (unreviewed) {
            hasChangedRef.current = true;
            setConfirmOpen({ type: "return-details", id: sale.return_id });
          } else {
            const refund = getSaleById(store.getState(), sale.return_id);
            if (refund) onSaleChange(refund);
          }
        }}
      />
    </>
  );
}

export function StatementDrillSalesTable({
  rows,
  onReload,
}: {
  rows: any[];
  onReload?: StatementDrillProps["onReload"];
}) {
  const [editSale, setEditSale] = useState<Sale | undefined>(undefined);
  const inventoryTypeIsCash =
    useSelector(userGetInventoryTypeSelector) === "cash";
  const columns = inventoryTypeIsCash ? SalesColumnsCash : SalesColumns;

  return (
    <>
      <StatementDrillSalesTableEditDialog
        sale={editSale}
        onSaleChange={(s: Sale) => setEditSale(s)}
        onClose={(reload?: boolean) => {
          if (reload && onReload) onReload();
          setEditSale(undefined);
        }}
        onReload={onReload}
      />
      <XGrid
        rows={rows}
        columns={columns}
        pagination
        disableSelectionOnClick
        components={{
          Toolbar: StatementGridTableToolbar,
        }}
        onCellDoubleClick={(params, ev) => setEditSale(params.row as Sale)}
      />
    </>
  );
}

function getTitle(type: StatementDrillType | undefined) {
  if (type) {
    if (type.type === "sales" && type.platform)
      return `Income - ${type.platform}`;

    if (type.type === "expense" && type.name)
      return `Cost of Sales - ${type.name}`;

    if (type.type === "transaction" && type.name)
      return `Other Business Expenses - ${type.name}`;
  }

  return "";
}

function StatementDrill({
  data,
  type,
  onReload,
  onClose,
  ...props
}: StatementDrillProps) {
  const open =
    type &&
    ((type.type === "sales" && type.platform) ||
      (type.type === "expense" && type.name) ||
      (type.type === "transaction" && type.name));

  return (
    <Details {...props} open={!!open} onClose={onClose} title={getTitle(type)}>
      {!open ? null : type.type === "sales" && type.platform ? (
        <Box height="75vh">
          {!data.salesListByDepartment?.[type.platform] ? (
            <EditableTransactionsXGrid
              rows={data.transactionsListByExpense?.[type.platform] || []}
              onReload={onReload}
            />
          ) : (
            <StatementDrillSalesTable
              rows={data.salesListByDepartment?.[type.platform] || []}
              onReload={onReload}
            />
          )}
        </Box>
      ) : type.type === "expense" && type.name ? (
        <ExpenseStatementDrill
          data={data}
          name={type.name}
          onReload={onReload}
        />
      ) : type.type === "transaction" && type.name ? (
        <Box height="75vh">
          <EditableTransactionsXGrid
            rows={data.transactionsListByExpense?.[type.name] || []}
            onReload={onReload}
          />
        </Box>
      ) : null}
    </Details>
  );
}

export default StatementDrill;
